import React, { useMemo } from 'react';
import { Box, Typography, styled } from '@mui/material';
import { AccessTime } from '@mui/icons-material';
import { SlateResponse } from '../common/store/types';
import { siteLogos, sportLogos } from '../utils/logos';

// Styled Components - Uniform blue styling to match MultipleSlatesCard
const CardContainer = styled(Box)<{ compact?: boolean }>(({ compact }) => ({
  backgroundColor: 'rgba(1, 90, 206, 0.2)',
  borderLeft: '3px solid #015ACE',
  borderRadius: 4,
  padding: compact ? '4px 8px' : '8px 12px',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: 'rgba(1, 90, 206, 0.3)',
    transform: 'translateX(2px)',
  },
}));

const SlateName = styled(Typography)<{ compact?: boolean }>(({ compact }) => ({
  fontFamily: "'Work Sans', sans-serif",
  fontSize: compact ? 11 : 13,
  fontWeight: 600,
  color: '#FFFFFF',
  lineHeight: 1.2,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  marginBottom: compact ? 2 : 4,
}));

const SlateInfo = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  flexWrap: 'wrap',
});

const InfoItem = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 4,
});

const InfoText = styled(Typography)<{ compact?: boolean }>(({ compact }) => ({
  fontFamily: "'Work Sans', sans-serif",
  fontSize: compact ? 9 : 10,
  fontWeight: 500,
  color: 'rgba(255, 255, 255, 0.6)',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
}));

const LogoBox = styled(Box)({
  width: 16,
  height: 16,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
  },
});

interface SlateCardProps {
  slate: SlateResponse;
  onClick: () => void;
  compact?: boolean;
  animationDelay?: number;
}

const SlateCard: React.FC<SlateCardProps> = ({
  slate,
  onClick,
  compact = false,
}) => {
  const formattedTime = useMemo(() => {
    const date = new Date(slate.startTime);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  }, [slate.startTime]);

  // Get display name - truncate if needed
  const displayName = useMemo(() => {
    const name = slate.name || slate.type || 'Slate';
    if (compact && name.length > 15) {
      return name.substring(0, 15) + '...';
    }
    return name;
  }, [slate.name, slate.type, compact]);

  const siteLogo = siteLogos[slate.site?.toLowerCase()];
  const sportLogo = sportLogos[slate.sport?.toUpperCase()];

  return (
    <CardContainer onClick={onClick} compact={compact}>
      <SlateName compact={compact}>{displayName}</SlateName>

      <SlateInfo>
        {siteLogo?.logo && (
          <LogoBox>
            <img src={siteLogo.logo} alt={slate.site} />
          </LogoBox>
        )}
        {sportLogo?.logo && (
          <LogoBox>
            <img src={sportLogo.logo} alt={slate.sport} />
          </LogoBox>
        )}
        <InfoItem>
          <AccessTime sx={{ fontSize: compact ? 10 : 12, color: 'rgba(255, 255, 255, 0.5)' }} />
          <InfoText compact={compact}>{formattedTime}</InfoText>
        </InfoItem>
      </SlateInfo>
    </CardContainer>
  );
};

export default SlateCard;
